import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCircleInfo } from '@fortawesome/sharp-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

import TrialBadge from '@/components/badge/trialBadge';
import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import IconButton from '@/components/button/iconButton';
import Mark from '@/components/icons/Mark';
import AcresToolTip from '@/components/tooltip';
import Typography from '@/components/typography';
import { HOW_DID_WE_DO } from '@/constants/strings';
import { MobileNavExpandButtons } from '@/features/navigation/mobile-nav/mobileNavExpandButtons';
import useBreakpoints, { BreakpointsEnum } from '@/hooks/useBreakpoint';

import BetaBadge from '../../../components/badge/betaBadge';
import InsightsMoreInfoToolTip from '../insightsTooltip';

const insightsHeaderToolTipId = 'insights-header-tooltip-id';

type Props = {
  title?: string;
  onXClick?: () => void;
  onBackButtonClick?: () => void;
  isBeta?: boolean;
  isTrial?: boolean;
  betaFeedbackLink?: string;
  titleRightAdornment?: React.ReactNode;
  backButtonText?: string;
  moreInfo?: {
    description: string;
    source?: string;
  };
  actionButton?: React.ReactNode;
  className?: string;
  header?: React.ReactNode;
  titleClassName?: string;
  titleHeader?: React.ReactNode;
};

/**
 * A generic header for insights that can be use to navigate and show titles
 */
const InsightsHeader: React.FC<Props> = ({
  title = undefined,
  onXClick = undefined,
  onBackButtonClick = undefined,
  backButtonText = undefined,
  isBeta = false,
  isTrial = false,
  moreInfo = undefined,
  betaFeedbackLink = undefined,
  titleRightAdornment = undefined,
  actionButton = undefined,
  className = undefined,
  header = null,
  titleClassName = undefined,
  titleHeader = undefined,
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { breakpoint } = useBreakpoints();
  const isMobile = breakpoint.down(BreakpointsEnum.SM);

  return (
    <div className={twMerge('flex flex-col gap-y-4 bg-white py-4', className)}>
      {mounted &&
        createPortal(
          <AcresToolTip id={insightsHeaderToolTipId}>
            <InsightsMoreInfoToolTip
              description={moreInfo?.description ?? ''}
              source={moreInfo?.source}
            />
          </AcresToolTip>,
          document.body
        )}
      {/* Top bar */}
      <div className="flex items-center space-x-4">
        <div className="flex flex-grow">
          {onBackButtonClick && (
            <Button
              className="flex gap-x-2 p-2 text-gray-70 hover:text-gray-100"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.GHOST}
              onClick={onBackButtonClick}
            >
              <FontAwesomeIcon className="h-5 w-5" icon={faArrowLeft} />
              {backButtonText}
            </Button>
          )}
          {titleHeader}
        </div>
        {!title && actionButton}
        {onXClick && !isMobile && (
          <IconButton
            size={ButtonSize.SMALL}
            variant={ButtonVariant.GHOST}
            onClick={onXClick}
          >
            <Mark />
          </IconButton>
        )}
        {isMobile && <MobileNavExpandButtons />}
      </div>

      {/* Main title section */}
      {title && (
        <div
          className={twMerge(
            'flex items-center justify-between gap-x-1',
            titleClassName
          )}
        >
          <div className="flex min-w-0 items-center gap-x-2">
            <Typography
              className="line-clamp-2 overflow-hidden break-words text-gray-100"
              title={title}
              variant="h4"
            >
              {title}
            </Typography>

            {isBeta && <BetaBadge />}
            {isTrial && <TrialBadge className="bg-green-50" />}

            {moreInfo && (
              <FontAwesomeIcon
                className="cursor-pointer text-gray-70 hover:text-gray-100"
                data-tooltip-delay-show={300}
                data-tooltip-id={insightsHeaderToolTipId}
                icon={faCircleInfo}
              />
            )}
            {titleRightAdornment}
          </div>

          {betaFeedbackLink && (
            <a
              className="text-green-50 underline"
              href={betaFeedbackLink}
              rel="noreferrer"
              target="_blank"
            >
              <Typography className="text-green-50" variant="caption">
                {HOW_DID_WE_DO}
              </Typography>
            </a>
          )}
          {actionButton}
        </div>
      )}
      {header}
    </div>
  );
};

export default InsightsHeader;
